import React, { useState, useEffect } from "react";
import axios from "axios";
import API from "../../constant/api";
import { ToastContainer, toast } from "react-toastify";
import ReactPaginate from "react-paginate";
import Modal from "react-modal";
import Button from "react-bootstrap/Button";
import "./ShippingDashboard.css";
import { shortName } from "../../helpers/displayName";

// Set app element for accessibility
Modal.setAppElement('#root');

const ShippingDashboard = (props) => {
  const PORT = API.PORTNUMBER;
  const [loading, setLoading] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [postsPerPage, setPostPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [statistics, setStatistics] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingModal, setTrackingModal] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [disputeConfirm, setDisputeConfirm] = useState({ open: false, orderId: null, orderStatus: null, order: null });
  const [disputeResolving, setDisputeResolving] = useState(false);
  const [reasonModal, setReasonModal] = useState({ open: false, reason: "", order: null });

  useEffect(() => {
    fetchDashboardData();
    fetchStatistics();
  }, [currentPage, statusFilter]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${PORT}/shipping/dashboard`, {
        page: currentPage + 1,
        limit: postsPerPage,
        status: statusFilter || undefined
      });

      if (response.data.status === "success") {
        setAllOrders(response.data.data.orders);
        setTotalPages(response.data.data.totalPages);
        setTotalOrders(response.data.data.total);
      } else {
        toast.error(response.data.message || "Failed to fetch orders");
      }
    } catch (error) {
      console.error('Fetch dashboard error:', error);
      toast.error("Failed to fetch shipping orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.post(`${PORT}/shipping/statistics`, {});
      if (response.data.status === "success") {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Fetch statistics error:', error);
    }
  };

  const handleResolveDispute = (orderId, orderStatus, order) => {
    setDisputeConfirm({ open: true, orderId, orderStatus, order });
  };

  const confirmResolveDispute = async () => {
    const { orderId, orderStatus } = disputeConfirm;
    setDisputeResolving(true);
    try {
      const response = await axios.post(`${PORT}/order/changeOrderStatus`, {
        orderId,
        orderStatus,
        disputeResolvedBy: "admin"
      });
      if (response.data.status === "success") {
        toast.success(`Dispute ${orderStatus === 8 ? 'Accepted — Buyer will be refunded' : 'Rejected — Order released to Seller'}.`);
        fetchDashboardData();
        fetchStatistics();
      } else {
        toast.error(response.data.message || "Failed to resolve dispute");
      }
    } catch (error) {
      console.error('Resolve dispute error:', error);
      toast.error("An error occurred while resolving the dispute");
    } finally {
      setDisputeResolving(false);
      setDisputeConfirm({ open: false, orderId: null, orderStatus: null, order: null });
    }
  };

  const handleRefreshTracking = async (orderId) => {
    try {
      const response = await axios.post(`${PORT}/shipping/manual-update`, {
        order_id: orderId
      });

      if (response.data.status === "success") {
        toast.success("Tracking updated successfully");
        fetchDashboardData();
      } else {
        // Handle test mode error
        if (response.data.message && response.data.message.includes("test tracking numbers")) {
          toast.info("Test mode: Only test tracking numbers (EZ1000000001, etc.) can be refreshed");
        } else {
          toast.error(response.data.message || "Failed to update tracking");
        }
      }
    } catch (error) {
      console.error('Refresh tracking error:', error);
      const errorMsg = error.response?.data?.message || error.message || "Failed to update tracking";

      // Handle test mode error
      if (errorMsg.includes("test tracking numbers")) {
        toast.info("ℹ️ Test Mode: Use test tracking numbers (EZ1000000001, EZ2000000002, etc.)");
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleViewTracking = async (order) => {
    setSelectedOrder(order);
    setTrackingModal(true);

    try {
      const response = await axios.post(`${PORT}/shipping/detailed-tracking`, {
        order_id: order._id
      });

      if (response.data.status === "success") {
        setTrackingData(response.data.data);
      } else {
        setTrackingData(null);
        toast.error(response.data.message || "No tracking information available");
      }
    } catch (error) {
      console.error('Fetch tracking error:', error);
      setTrackingData(null);
    }
  };

  // Admin-only endpoint, so download with the admin token (a plain link cannot send it).
  const handlePrintLabel = async (labelUrl, orderNumber) => {
    if (!labelUrl) return;
    const downloadUrl = `${PORT}/shipping/download-label?url=${encodeURIComponent(labelUrl)}&id=${encodeURIComponent(orderNumber)}`;
    try {
      const response = await fetch(downloadUrl, { headers: { 'x-access-token': localStorage.getItem('token') || '' } });
      if (!response.ok) throw new Error(`Label download failed: ${response.status}`);
      const disposition = response.headers.get('Content-Disposition') || '';
      const name = (disposition.match(/filename="([^"]+)"/) || [])[1] || `Shipping_Label_${orderNumber || 'Order'}.pdf`;
      const url = window.URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Print label error:', error);
      toast.error('Could not download the label.');
    }
  };

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { text: 'Pending', class: 'badge-secondary' },
      1: { text: 'Paid', class: 'badge-info' },
      2: { text: 'Completed', class: 'badge-success' },
      3: { text: 'Cancelled', class: 'badge-danger' },
      4: { text: 'Shipped', class: 'badge-primary' },
      5: { text: 'Out for Delivery', class: 'badge-warning' },
      6: { text: 'Delivered', class: 'badge-success' },
      7: { text: 'Returned', class: 'badge-danger' },
      8: { text: 'Refunded (Buyer)', class: 'badge-info' },
      9: { text: 'Resolved (Seller)', class: 'badge-success' },
      14: { text: 'Dispute Requested', class: 'badge-danger' }
    };
    const statusInfo = statusMap[status] || { text: 'Unknown', class: 'badge-secondary' };
    return `<span class="badge ${statusInfo.class}">${statusInfo.text}</span>`;
  };

  const handleCloseTrackingModal = () => {
    setTrackingModal(false);
    setSelectedOrder(null);
    setTrackingData(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCourierService = (courierService) => {
    if (!courierService) return 'N/A';

    // Common carrier mappings
    const carrierMap = {
      'usps': 'USPS',
      'ups': 'UPS',
      'fedex': 'FedEx',
      'dhl': 'DHL',
      'canada_post': 'Canada Post',
      'royal_mail': 'Royal Mail',
      'australia_post': 'Australia Post'
    };

    // Try to extract carrier from service ID
    const serviceStr = courierService.toString().toLowerCase();

    // Find matching carrier
    for (const [key, value] of Object.entries(carrierMap)) {
      if (serviceStr.includes(key)) {
        return (
          <span className="courier-badge">
            <i className={`fa fa-${getCarrierIcon(key)}`}></i>
            {value}
          </span>
        );
      }
    }

    // If no match, return as-is with formatting
    return (
      <span className="courier-badge">
        <i className="fa fa-shipping-fast"></i>
        {courierService}
      </span>
    );
  };

  const getCarrierIcon = (carrier) => {
    const icons = {
      'usps': 'fa-shipping-fast',
      'ups': 'fa-truck',
      'fedex': 'fa-shipping-fast',
      'dhl': 'fa-shipping-fast',
      'canada_post': 'fa-mail-bulk',
      'royal_mail': 'fa-mail-bulk',
      'australia_post': 'fa-shipping-fast'
    };
    return icons[carrier] || 'fa-shipping-fast';
  };

  return (
    <div className="shipping-dashboard-container">
      <div className="workspace_title_hd">
        <div className="row">
          <div className="col-md-12">
            <div className="titleof_page">
              <ul className="breadcrumb_list topsmall_bottom">
                <li><a href="javascript:void(0)">Admin Panel</a></li>
                <li><i className="fa fa-angle-right" style={{ padding: '5px' }}></i></li>
                <li>Shipping Dashboard</li>
              </ul>
              <h2 className="main_title">Shipping Management</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="shipping-stats-row">
          <div className="stat-card">
            <div className="stat-icon total">
              <i className="fa fa-box"></i>
            </div>
            <div className="stat-info">
              <h3>{statistics.total_orders}</h3>
              <p>Total Orders</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon shipped">
              <i className="fas fa-shipping-fast"></i>
            </div>
            <div className="stat-info">
              <h3>{statistics.shipped_orders}</h3>
              <p>Shipped</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon transit">
              <i className="fa fa-truck"></i>
            </div>
            <div className="stat-info">
              <h3>{statistics.in_transit_orders}</h3>
              <p>In Transit</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon delivered">
              <i className="fa fa-check-circle"></i>
            </div>
            <div className="stat-info">
              <h3>{statistics.delivered_orders}</h3>
              <p>Delivered</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon total" style={{ backgroundColor: '#ff4d4f' }}>
              <i className="fa fa-exclamation-circle" style={{ color: '#fff' }}></i>
            </div>
            <div className="stat-info">
              <h3>{statistics.disputed_orders || 0}</h3>
              <p>Disputed</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="shipping-filters">
        <div className="filter-group">
          <label>Status Filter:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(0);
            }}
            className="form-control"
          >
            <option value="">All Orders</option>
            <option value="0">Pending</option>
            <option value="1">Paid</option>
            <option value="4">Shipped</option>
            <option value="5">Out for Delivery</option>
            <option value="6">Delivered</option>
            <option value="3">Cancelled</option>
            <option value="7">Returned</option>
            <option value="14">Dispute Requested</option>
          </select>
        </div>
        <button onClick={fetchDashboardData} className="btn-refresh">
          <i className="fa fa-sync"></i> Refresh
        </button>
      </div>

      {/* Orders Table */}
      <div className="main_oflistingForm bg_light_gray">
        <div className="inner_listing_formS">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading shipping orders...</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="shipping-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Product</th>
                    <th>Buyer</th>
                    <th>Seller</th>
                    <th>Courier Partner</th>
                    <th>Tracking #</th>
                    <th>Service/Rate</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allOrders.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="no-data">
                        No shipping orders found
                      </td>
                    </tr>
                  ) : (
                    allOrders.map((order) => (
                      <tr key={order._id}>
                        <td className="order-number">{order.order_number}</td>
                        <td>
                          <div className="product-info">
                            {order.product?.productName || 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div className="user-info">
                            {shortName(order.buyer?.firstName, order.buyer?.lastName) || 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div className="user-info">
                            {shortName(order.seller?.firstName, order.seller?.lastName) || 'N/A'}
                          </div>
                        </td>
                        <td>
                          {order.courier_partner ? (
                            <div className="courier-badge">
                              <i className="fa fa-handshake mr-1"></i>
                              {order.courier_partner.name}
                            </div>
                          ) : (
                            <span className="text-muted small">Generic / External</span>
                          )}
                          {order.label_paid && (
                            <div className="status-badge status-paid mt-1" style={{ fontSize: '10px', padding: '2px 6px' }}>
                              <i className="fa fa-check-circle mr-1"></i> Paid £{order.label_paid_amount || order.shipping_rate}
                            </div>
                          )}
                        </td>
                        <td>
                          {order.tracking_number ? (
                            <code className="tracking-code">{order.tracking_number}</code>
                          ) : (
                            <span className="no-tracking">No tracking</span>
                          )}
                        </td>
                        <td>
                          {order.carrier ? (
                            <div className="courier-display">
                              {formatCourierService(order.carrier)}
                              <small className="service-name d-block mt-1">
                                {order.service?.replace(/_/g, ' ')}
                                {order.shipping_rate ? ` (£${order.shipping_rate})` : ''}
                              </small>
                            </div>
                          ) : 'N/A'}
                        </td>
                        <td dangerouslySetInnerHTML={{ __html: getStatusBadge(order.orderStatus) }} />
                        <td>{formatDate(order.created_at)}</td>
                        <td>
                          <div className="action-buttons">
                            {order.label_url && (
                              <button
                                onClick={() => handlePrintLabel(order.label_url, order.order_number || order._id.slice(-8))}
                                className="btn-action btn-print"
                                title="Download/Print Shipping Label"
                              >
                                <i className="fa fa-print"></i>
                              </button>
                            )}
                            {order.tracking_number && (
                              <button
                                onClick={() => handleViewTracking(order)}
                                className="btn-action btn-view"
                                title="View Tracking Details"
                              >
                                <i className="fa fa-search"></i>
                              </button>
                            )}
                            {order.tracking_number && (
                              <button
                                onClick={() => handleRefreshTracking(order._id)}
                                className="btn-action btn-refresh-small"
                                title={order.tracking_number && order.tracking_number.startsWith('EZ') ? "Refresh Tracking" : "Test Mode: Only works with test tracking numbers"}
                              >
                                <i className="fa fa-sync"></i>
                              </button>
                            )}
                            {order.orderStatus === 14 && (
                              <div className="btn-group ml-1">
                                <button
                                  onClick={() => setReasonModal({ open: true, reason: order.disputeReason || 'No reason provided', order })}
                                  className="btn-action"
                                  title="View Dispute Reason"
                                  style={{ backgroundColor: '#1890ff', color: 'white' }}
                                >
                                  <i className="fa fa-info-circle"></i>
                                </button>
                                <button
                                  onClick={() => handleResolveDispute(order._id, 8, order)} 
                                  className="btn-action"
                                  title="Accept Dispute (Refund Buyer)"
                                  style={{ backgroundColor: '#52c41a', color: 'white' }}
                                >
                                  <i className="fa fa-check"></i>
                                </button>
                                <button
                                  onClick={() => handleResolveDispute(order._id, 9, order)}
                                  className="btn-action"
                                  title="Reject Dispute (Release to Seller)"
                                  style={{ backgroundColor: '#f5222d', color: 'white' }}
                                >
                                  <i className="fa fa-times"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination - Separate Row */}
      {totalPages > 1 && (
        <div className="shipping-pagination-row">
          <div className="showingflexx">
            <div className="showingleft">
              Showing {currentPage + 1} of {totalPages}
            </div>
            <div className="left_paginistion">
              <ReactPaginate
                previousLabel={"Previous"}
                nextLabel={"Next"}
                pageCount={totalPages}
                containerClassName={"paginationBttns"}
                previousLinkClassName={"previousBttn"}
                nextLinkClassName={"nextBttn"}
                disabledClassName={"paginationDisabled"}
                activeClassName={"paginationActive"}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* Tracking Modal */}
      <Modal
        isOpen={trackingModal}
        onRequestClose={handleCloseTrackingModal}
        className="tracking-modal-content"
        overlayClassName="tracking-modal-overlay"
                shouldCloseOnOverlayClick={true}
                shouldCloseOnEsc={true}>
        <div className="modal-header">
          <h2>Tracking Details</h2>
          <button onClick={handleCloseTrackingModal} className="close-btn">
            <i className="fa fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          {selectedOrder && (
            <div className="tracking-modal-info">
              <div className="info-row">
                <strong>Order:</strong> {selectedOrder.order_number}
              </div>
              {selectedOrder.tracking_number && (
                <div className="info-row">
                  <strong>Tracking:</strong> {selectedOrder.tracking_number}
                </div>
              )}
              {selectedOrder.label_url && (
                <div className="info-row">
                  <strong>Shipping Label:</strong>
                  <button onClick={() => handlePrintLabel(selectedOrder.label_url, selectedOrder.order_number || selectedOrder._id.slice(-8))} className="btn-print-modal" style={{ border: 'none', background: 'none', padding: 0, color: '#249ee4', cursor: 'pointer', textDecoration: 'underline' }}>
                    Download/Print Label <i className="fa fa-print"></i>
                  </button>
                </div>
              )}

              {trackingData ? (
                <div className="tracking-timeline-modal">
                  <div className="tracking-status-header">
                    <span className={`status-badge status-${trackingData.status}`}>
                      {trackingData.status?.replace('_', ' ').toUpperCase()}
                    </span>
                    {trackingData.est_delivery_date && (
                      <span className="delivery-date">
                        Est. Delivery: {formatDate(trackingData.est_delivery_date)}
                      </span>
                    )}
                  </div>

                  {trackingData.tracking_details && trackingData.tracking_details.length > 0 && (
                    <div className="tracking-history">
                      <h4>Tracking History</h4>
                      {trackingData.tracking_details.map((detail, idx) => (
                        <div key={idx} className="tracking-event">
                          <div className="event-time">{formatDate(detail.datetime)}</div>
                          <div className="event-status">{detail.status}</div>
                          <div className="event-message">{detail.message}</div>
                          {detail.location && (
                            <div className="event-location">
                              <i className="fa fa-map-marker-alt"></i> {detail.location}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-tracking-data">
                  <i className="fa fa-info-circle"></i>
                  <p>No detailed tracking information available</p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button onClick={handleCloseTrackingModal} className="btn-secondary">
            Close
          </Button>
        </div>
      </Modal>

      <ToastContainer />

      {/* Dispute Confirm Dialog */}
      {disputeConfirm.open && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', maxWidth: '480px', width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #34c1c6, #2a9da1)',
              padding: '22px 24px', color: '#fff'
            }}>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: '18px' }}>
                <i className={`fa ${disputeConfirm.orderStatus === 8 ? 'fa-check-circle' : 'fa-times-circle'} mr-2`}></i>
                {disputeConfirm.orderStatus === 8 ? 'Accept Dispute' : 'Reject Dispute'}
              </h5>
              <p style={{ margin: '6px 0 0', fontSize: '13px', opacity: 0.85 }}>
                {disputeConfirm.orderStatus === 8
                  ? 'This will refund the buyer and close the dispute.'
                  : 'This will release the payment to the seller and close the dispute.'}
              </p>
            </div>
            {/* Body */}
            <div style={{ padding: '24px' }}>
              {disputeConfirm.order && (
                <div style={{ background: '#f0fafa', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', borderLeft: '4px solid #34c1c6' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#555', lineHeight: 1.7 }}>
                    <strong>Order:</strong> {disputeConfirm.order.order_number}<br />
                    <strong>Dispute Reason:</strong> {disputeConfirm.order.disputeReason || 'No reason provided'}
                  </p>
                </div>
              )}
              <p style={{ fontSize: '15px', color: '#333', margin: 0, fontWeight: 500 }}>
                Are you sure you want to <strong>{disputeConfirm.orderStatus === 8 ? 'accept' : 'reject'}</strong> this dispute?
                This action <strong>cannot be undone</strong>.
              </p>
            </div>
            {/* Footer */}
            <div style={{ display: 'flex', gap: '12px', padding: '16px 24px', borderTop: '1px solid #f0f0f0', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDisputeConfirm({ open: false, orderId: null, orderStatus: null, order: null })}
                disabled={disputeResolving}
                style={{ padding: '9px 22px', borderRadius: '8px', border: '1px solid #d9d9d9', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmResolveDispute}
                disabled={disputeResolving}
                style={{
                  padding: '9px 22px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                  fontWeight: 700, fontSize: '14px', color: '#fff',
                  background: '#34c1c6',
                  opacity: disputeResolving ? 0.7 : 1
                }}
              >
                {disputeResolving
                  ? <><i className="fa fa-spinner fa-spin mr-1"></i> Processing...</>
                  : disputeConfirm.orderStatus === 8 ? <><i className="fa fa-check mr-1"></i> Yes, Accept & Refund</> : <><i className="fa fa-times mr-1"></i> Yes, Reject</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {reasonModal.open && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', maxWidth: '480px', width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #1890ff, #096dd9)',
              padding: '22px 24px', color: '#fff'
            }}>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: '18px' }}>
                <i className="fa fa-info-circle mr-2"></i> Dispute Reason
              </h5>
              <p style={{ margin: '6px 0 0', fontSize: '13px', opacity: 0.85 }}>
                Details provided by the buyer for this dispute case.
              </p>
            </div>
            <div style={{ padding: '24px' }}>
              {reasonModal.order && (
                <div className="mb-3" style={{ borderBottom: '1px solid #f0f0f0', pb: '12px', mb: '16px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#888' }}>
                    <strong>Order Number:</strong> {reasonModal.order.order_number}
                  </p>
                </div>
              )}
              <div style={{
                background: '#f9f9f9', borderRadius: '10px', padding: '16px',
                borderLeft: '4px solid #1890ff'
              }}>
                <p style={{ margin: 0, fontSize: '15px', color: '#333', lineHeight: 1.6 }}>
                  {reasonModal.reason}
                </p>
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f0f0', textAlign: 'right' }}>
              <button
                onClick={() => setReasonModal({ open: false, reason: "", order: null })}
                style={{
                  padding: '9px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                  fontWeight: 700, fontSize: '14px', color: '#fff',
                  background: '#1890ff'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingDashboard;
